import { Injectable } from '@angular/core';
import { BehaviorSubject, tap } from 'rxjs';
import { ProgramMarketingDetailModel } from '@pages/sales-marketing/program-marketing/shared/program-marketing-detail.model';
import {
  EnumProgramMarketingSection,
  EnumProgramMarketingStatus,
  EnumProgramMarketingType,
  EnumVerificationStatus,
} from '@pages/sales-marketing/program-marketing-legacy/program-marketing.enum';
import { BaseService } from '@services/base-service.service';
import { RolePrivilegeService } from '@services/role-privilege.service';
import { Router } from '@angular/router';
import { API } from '@config/constants/api.constant';
import { map } from 'rxjs/operators';
import { AuthService } from '@services/auth.service';
import {
  IProgramMarketingDetail,
  IProgramMarketingDetailLog,
  RevisionNoteSession,
} from '@pages/sales-marketing/program-marketing/program-marketing-distributor/program-marketing-distributor-interface';
import { IPayloadExtendPeriod, IPayloadProgramVerification, IResponseProgramMarketing } from '@pages/sales-marketing/program-marketing-legacy/program-marketing-legacy.interface';
import { IGenericLockSystem, IGenericPayloadLockSystem } from '@shared/interface/generic';
import { EnumSystemLockType } from '@config/enum/system.enum';

@Injectable({
  providedIn: 'root',
})
export class ProgramMarketingDistributorService {
  private detailProgramMarketingSubject = new BehaviorSubject<IProgramMarketingDetail>(new ProgramMarketingDetailModel<IProgramMarketingDetail>().Data);
  detailProgramMarketing$ = this.detailProgramMarketingSubject.asObservable();

  private detailProgramLogSubject = new BehaviorSubject<IProgramMarketingDetailLog>(new ProgramMarketingDetailModel<IProgramMarketingDetailLog>().Data);
  detailProgramLog$ = this.detailProgramLogSubject.asObservable();

  private viewModeSubject = new BehaviorSubject<'detail' | 'log'>('detail');
  private programLockSubject = new BehaviorSubject<IGenericLockSystem>({ can_access: false });

  get ProgramLock() {
    return this.programLockSubject.value;
  }

  get DetailProgramMarketing() {
    return this.detailProgramMarketingSubject.value;
  }

  set DetailProgramMarketing(data: IProgramMarketingDetail) {
    this.detailProgramMarketingSubject.next(data);
  }

  get DetailProgramLog() {
    return this.detailProgramLogSubject.value;
  }

  get IsProgramStatusSubmitted() {
    const _detailProgramHeader = this.ViewMode === 'log' ? this.DetailProgramLog?.header : this.DetailProgramMarketing?.header;
    return _detailProgramHeader && _detailProgramHeader.status_program_marketing_enum === EnumProgramMarketingStatus.SUBMITTED;
  }

  get IsProgramStatusExtendPeriod() {
    const _detailProgramHeader = this.ViewMode === 'log' ? this.DetailProgramLog?.header : this.DetailProgramMarketing?.header;
    return _detailProgramHeader && _detailProgramHeader.status_program_marketing_enum === EnumProgramMarketingStatus.EXTEND_PERIOD;
  }

  get IsProgramStatusScheduled() {
    return this.DetailProgramMarketing.header.status_program_marketing_enum === EnumProgramMarketingStatus.SCHEDULED;
  }

  get IsProgramStatusNeedChanged() {
    return this.DetailProgramMarketing.header.status_program_marketing_enum === EnumProgramMarketingStatus.NEED_CHANGED;
  }

  get IsProgramStatusFinished() {
    return this.DetailProgramMarketing.header.status_program_marketing_enum === EnumProgramMarketingStatus.FINISHED;
  }

  get IsProgramStatusActive() {
    return this.DetailProgramMarketing.header.status_program_marketing_enum === EnumProgramMarketingStatus.ACTIVE;
  }

  get ProgramMarketingType() {
    return this.ViewMode === 'log'
      ? this.DetailProgramLog && (this.DetailProgramLog.information.before?.program_type_enum as EnumProgramMarketingType)
      : this.DetailProgramMarketing && this.DetailProgramMarketing.information.program_type_enum;
  }

  get ProgramMarketingStatus() {
    return this.ViewMode === 'log' ? this.DetailProgramLog?.header?.status_program_marketing_enum : this.DetailProgramMarketing?.header?.status_program_marketing_enum;
  }

  get ProgramDocumentUrl() {
    return this.ViewMode === 'log' ? this.DetailProgramLog.document_url : this.DetailProgramMarketing.document_url;
  }

  get ProgramDocumentExtendUrl() {
    return this.ViewMode === 'log' ? this.DetailProgramLog.document_extend : this.DetailProgramMarketing.document_extend;
  }

  get ProgramStartDate() {
    return this.ViewMode === 'log' ? this.DetailProgramLog?.information?.before?.period_start : this.DetailProgramMarketing?.information?.period_start;
  }

  get ProgramEndDate() {
    return this.ViewMode === 'log' ? this.DetailProgramLog?.information?.before?.period_end : this.DetailProgramMarketing?.information?.period_end;
  }

  get ViewMode() {
    return this.viewModeSubject.value;
  }

  set ViewMode(mode: 'detail' | 'log') {
    this.viewModeSubject.next(mode);
  }

  get IsCurrentViewModeLog() {
    return this.ViewMode === 'log';
  }

  constructor(
    private baseService: BaseService,
    private rolePrivilegeService: RolePrivilegeService,
    private router: Router,
    private authService: AuthService
  ) {}

  getProgramMarketingDetail(id: string, programType: EnumProgramMarketingType) {
    const endpoint = this.getProgramEndpoint(programType);
    return this.baseService.getData<IProgramMarketingDetail>(endpoint + id).pipe(
      map((resp) => resp && resp.data),
      tap((data) => this.detailProgramMarketingSubject.next(data))
    );
  }

  setSessionRevisionNote(data: IPayloadProgramVerification) {
    sessionStorage.setItem('revisionNote', JSON.stringify(data));
  }

  isSectionVerified = (status?: EnumVerificationStatus | null) => (status ? status === EnumVerificationStatus.VERIFIED : false);

  isSectionRequestRevision = (status?: EnumVerificationStatus | null) => (status ? status === EnumVerificationStatus.REQUEST_REVISION : false);

  isSectionVerifiedOrRequestRevision = (status?: EnumVerificationStatus | null) =>
    status ? status === EnumVerificationStatus.REQUEST_REVISION || status === EnumVerificationStatus.VERIFIED : false;

  updateSectionVerification(
    programID: string,
    payload: {
      program_marketing_id: string;
      type: EnumProgramMarketingSection;
      status: EnumVerificationStatus | null;
      id_schema_promotion: string | null;
    }
  ) {
    return this.baseService.putData<any>(API.PROGRAM_MARKETING.UPDATE_VERIFICATION_STATUS, payload);
  }

  getPrivilegeCTAVerifyProgram() {
    return this.rolePrivilegeService.checkPrivilege('SALES_MARKETING_PROGRAM', 'PROGRAM_MARKETING_SETTING', 'CTA_VERIFY_PROGRAM');
  }

  getPrivilegeCTARequestRevision() {
    return this.rolePrivilegeService.checkPrivilege('SALES_MARKETING_PROGRAM', 'PROGRAM_MARKETING_SETTING', 'CTA_REQUEST_REVISION_PROGRAM');
  }

  getPrivilegeCTAConfirmProgram = (): boolean => this.rolePrivilegeService.checkPrivilege('SALES_MARKETING_PROGRAM', 'PROGRAM_MARKETING_SETTING', 'CTA_CONFIRM_PROGRAM');

  getPrivilegeCTAEditProgram = (): boolean => this.rolePrivilegeService.checkPrivilege('SALES_MARKETING_PROGRAM', 'PROGRAM_MARKETING_SETTING', 'CTA_EDIT_PROGRAM');

  getProgramEndpoint(type: EnumProgramMarketingType, isLog = false) {
    let endpoint = '';

    switch (type) {
      case EnumProgramMarketingType.ONE_SHOOT:
        endpoint = isLog ? API.PROGRAM_MARKETING.GET_LOG_DETAIL.ONE_SHOOT : API.PROGRAM_MARKETING.DETAIL_PROGRAM.ONE_SHOOT;
        break;
      case EnumProgramMarketingType.DISCOUNT_PURCHASE:
        endpoint = isLog ? API.PROGRAM_MARKETING.GET_LOG_DETAIL.DISCOUNT_PURCHASE : API.PROGRAM_MARKETING.DETAIL_PROGRAM.DISCOUNT_PURCHASE;
        break;
      case EnumProgramMarketingType.DISCOUNT_PRODUCT:
        endpoint = isLog ? API.PROGRAM_MARKETING.GET_LOG_DETAIL.DISCOUNT_PRODUCT : API.PROGRAM_MARKETING.DETAIL_PROGRAM.PRODUCT_DISCOUNT;
        break;
      case EnumProgramMarketingType.PRODUCT_COMPENSATION:
        endpoint = API.PROGRAM_MARKETING.DETAIL_PROGRAM.PRODUCT_COMPENSATION;
        break;
    }

    return endpoint;
  }

  postExtendPeriod(programID: string, payload: IPayloadExtendPeriod) {
    return this.baseService.postData<IResponseProgramMarketing>(API.PROGRAM_MARKETING.POST_EXTEND_PERIOD + programID, payload);
  }

  async redirectToDetail(programID: string, type: any) {
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    return await this.router.navigate([`/sales-marketing/program-marketing/${programID}`], {
      queryParams: {
        type: type,
      },
    });
  }

  async redirectToList() {
    return this.router.navigate(['/sales-marketing/program-marketing/list']);
  }

  getDetailLog(programID: string, programType: EnumProgramMarketingType) {
    const endpoint = this.getProgramEndpoint(programType, true);
    return this.baseService.getData<IProgramMarketingDetailLog>(endpoint + programID).pipe(
      map((resp) => resp && resp.data),
      tap((data) => this.detailProgramLogSubject.next(data))
    );
  }

  postSubmitProgramVerification(promagID: string, payload: { revision_note: RevisionNoteSession }) {
    // BE expects key `compensation_term`, bukan `compensation` - satu-satunya section yang beda nama dari FE
    const { compensation, ...revisionNote } = payload.revision_note;
    const wirePayload = { revision_note: { ...revisionNote, compensation_term: compensation } };

    return this.baseService.postData<IResponseProgramMarketing>(API.PROGRAM_MARKETING.POST_SUBMIT_VERIFICATION + promagID, wirePayload);
  }

  checkProgramLock(programID: string) {
    return this.baseService.getData<IGenericLockSystem>(API.LOCKING_SYSTEM.CHECKING + programID).pipe(
      map((resp) => resp && resp.data),
      tap((data) => this.programLockSubject.next(data))
    );
  }

  setProgramLockStatus(programID: string, isLock: boolean) {
    const payload = {
      reference_id: programID,
      type: EnumSystemLockType.PROGRAM_ORDER,
    };
    const endpoint = isLock ? API.LOCKING_SYSTEM.LOCK : API.LOCKING_SYSTEM.UNLOCK;
    return this.baseService.postData<IGenericPayloadLockSystem>(endpoint, payload).pipe(
      map((resp) => resp && resp.data),
      tap(() => {
        const currentLock = this.ProgramLock;
        this.programLockSubject.next({ ...currentLock, is_locking: isLock });
      })
    );
  }

  unlockProgramViaFetch(programID: string) {
    if (!programID) return;

    const url = API.BASE + API.LOCKING_SYSTEM.UNLOCK;
    const token = this.authService.getUserToken();
    const payload = { reference_id: programID, type: EnumSystemLockType.PROGRAM_ORDER };

    fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  }
}
